import React, { useCallback, useEffect, useState } from "react";
import { caApi } from "../../../../../backend_urls/caApi";
import PaymentRequestModal from "./PaymentRequestModal";

const PaymentRequestManager = ({
  children,

  /*
   * Controls which server data this manager should load.
   *
   * ClientPaymentsPage:
   * - paymentOverview
   * - platformOverview
   * - paymentRequests
   *
   * AllClientPaymentsRequestsPage:
   * - paymentRequests
   */
  loadOverview = false,
  loadPlatformOverview: shouldLoadPlatformOverview = false,
  loadRequests = true,

  /*
   * Parent callback after any mutation.
   */
  onDataChanged,
}) => {
  /*
   * ==========================================================================
   * SERVER STATE
   * ==========================================================================
   */

  const [paymentOverview, setPaymentOverview] = useState(null);
  const [platformOverview, setPlatformOverview] = useState(null);
  const [paymentRequests, setPaymentRequests] = useState([]);

  /*
   * ==========================================================================
   * LOADING
   * ==========================================================================
   */

  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingPlatformOverview, setLoadingPlatformOverview] = useState(false);
  const [loadingPaymentRequests, setLoadingPaymentRequests] = useState(false);

  const [loadingRequest, setLoadingRequest] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [deletingRequest, setDeletingRequest] = useState(false);

  /*
   * ==========================================================================
   * ERROR
   * ==========================================================================
   */

  const [error, setError] = useState(null);

  /*
   * ==========================================================================
   * MODAL
   * ==========================================================================
   */

  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState(null);

  const [isPaymentRequestModalOpen, setIsPaymentRequestModalOpen] =
    useState(false);

  /*
   * ==========================================================================
   * LOAD PAYMENT OVERVIEW
   * ==========================================================================
   */

  const loadPaymentOverview = useCallback(async () => {
    try {
      setLoadingOverview(true);

      const response = await caApi.getPaymentOverview();

      const data = response?.data ?? null;

      setPaymentOverview(data);

      return data;
    } catch (err) {
      console.error("Failed to load payment overview:", err);

      throw err;
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  /*
   * ==========================================================================
   * LOAD PLATFORM OVERVIEW
   * ==========================================================================
   */

  const loadPlatformOverview = useCallback(async () => {
    try {
      setLoadingPlatformOverview(true);

      const response = await caApi.getPlatformOverview();

      const data = response?.data ?? null;

      setPlatformOverview(data);

      return data;
    } catch (err) {
      console.error("Failed to load platform overview:", err);

      throw err;
    } finally {
      setLoadingPlatformOverview(false);
    }
  }, []);

  /*
   * ==========================================================================
   * LOAD PAYMENT REQUESTS
   * ==========================================================================
   */

  const loadPaymentRequests = useCallback(async () => {
    try {
      setLoadingPaymentRequests(true);

      const response = await caApi.getAllPaymentRequests();

      const responseData = response?.data;

      let requests = [];

      if (Array.isArray(responseData)) {
        requests = responseData;
      } else if (Array.isArray(responseData?.results)) {
        requests = responseData.results;
      } else if (Array.isArray(responseData?.data)) {
        requests = responseData.data;
      }

      setPaymentRequests(requests);

      return requests;
    } catch (err) {
      console.error("Failed to load payment requests:", err);

      throw err;
    } finally {
      setLoadingPaymentRequests(false);
    }
  }, []);

  /*
   * ==========================================================================
   * INITIAL LOAD
   * ==========================================================================
   */

  const loadInitialData = useCallback(async () => {
    const requests = [];

    if (loadOverview) {
      requests.push(loadPaymentOverview());
    }

    if (shouldLoadPlatformOverview) {
      requests.push(loadPlatformOverview());
    }

    if (loadRequests) {
      requests.push(loadPaymentRequests());
    }

    if (!requests.length) {
      return;
    }

    setError(null);

    const results = await Promise.allSettled(requests);

    const failed = results.find((result) => result.status === "rejected");

    if (failed) {
      setError("Unable to load payment information.");
    }

    return results;
  }, [
    loadOverview,
    shouldLoadPlatformOverview,
    loadRequests,
    loadPaymentOverview,
    loadPlatformOverview,
    loadPaymentRequests,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /*
   * ==========================================================================
   * REFRESH AFTER MUTATION
   * ==========================================================================
   */

  const notifyParentOfChange = useCallback(async () => {
    const refreshes = [];

    if (loadOverview) {
      refreshes.push(loadPaymentOverview());
    }

    if (shouldLoadPlatformOverview) {
      refreshes.push(loadPlatformOverview());
    }

    if (loadRequests) {
      refreshes.push(loadPaymentRequests());
    }

    if (refreshes.length) {
      await Promise.allSettled(refreshes);
    }

    if (typeof onDataChanged === "function") {
      await onDataChanged();
    }
  }, [
    loadOverview,
    shouldLoadPlatformOverview,
    loadRequests,
    loadPaymentOverview,
    loadPlatformOverview,
    loadPaymentRequests,
    onDataChanged,
  ]);

  /*
   * ==========================================================================
   * OPEN CREATE
   * ==========================================================================
   */

  const openCreate = useCallback(() => {
    setSelectedPaymentRequest(null);
    setIsPaymentRequestModalOpen(true);
  }, []);

  /*
   * ==========================================================================
   * CLOSE MODAL
   * ==========================================================================
   */

  const closeModal = useCallback(() => {
    setIsPaymentRequestModalOpen(false);
    setSelectedPaymentRequest(null);
  }, []);

  /*
   * ==========================================================================
   * OPEN EXISTING REQUEST
   * ==========================================================================
   */

  const openExisting = useCallback(async (row) => {
    if (!row?.id) {
      console.error(
        "Cannot open payment request because the request ID is missing.",
      );

      return;
    }

    try {
      setLoadingRequest(true);
      setError(null);

      setIsPaymentRequestModalOpen(false);
      setSelectedPaymentRequest(null);

      const response = await caApi.retrievePaymentRequest(row.id);

      const paymentRequest = response?.data;

      if (!paymentRequest) {
        throw new Error("Payment request data was not returned by the server.");
      }

      setSelectedPaymentRequest(paymentRequest);
      setIsPaymentRequestModalOpen(true);

      return paymentRequest;
    } catch (err) {
      console.error("Failed to retrieve payment request:", err);

      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Unable to load payment request.";

      setError(message);

      window.alert(message);

      return null;
    } finally {
      setLoadingRequest(false);
    }
  }, []);

  /*
   * ==========================================================================
   * CREATE PAYMENT REQUEST
   * ==========================================================================
   */

  const createPaymentRequest = useCallback(
    async (data) => {
      try {
        setSavingRequest(true);
        setError(null);

        const response = await caApi.createPaymentRequest(data);

        await notifyParentOfChange();

        return response?.data;
      } catch (err) {
        console.error("Failed to create payment request:", err);

        setError(
          err?.response?.data?.detail || "Unable to create payment request.",
        );

        throw err;
      } finally {
        setSavingRequest(false);
      }
    },
    [notifyParentOfChange],
  );

  /*
   * ==========================================================================
   * CREATE PAYMENT REQUEST DRAFT
   * ==========================================================================
   */

  const createPaymentRequestDraft = useCallback(
    async (data) => {
      try {
        setSavingRequest(true);
        setError(null);

        const response = await caApi.createPaymentRequestDraft(data);

        await notifyParentOfChange();

        return response?.data;
      } catch (err) {
        console.error("Failed to save payment request draft:", err);

        setError(
          err?.response?.data?.detail ||
            "Unable to save payment request draft.",
        );

        throw err;
      } finally {
        setSavingRequest(false);
      }
    },
    [notifyParentOfChange],
  );

  /*
   * ==========================================================================
   * RETRIEVE PAYMENT REQUEST
   * ==========================================================================
   */

  const retrievePaymentRequest = useCallback(async (id) => {
    if (!id) {
      throw new Error("Payment request ID is required.");
    }

    const response = await caApi.retrievePaymentRequest(id);

    return response?.data;
  }, []);

  /*
   * ==========================================================================
   * UPDATE PAYMENT REQUEST AS DRAFT
   * ==========================================================================
   */

  const updatePaymentRequestDraft = useCallback(
    async (id, data) => {
      if (!id) {
        throw new Error("Payment request ID is required.");
      }

      try {
        setSavingRequest(true);
        setError(null);

        console.log("updated send to server is=", data);

        const response = await caApi.updatePaymentRequestDraft(id, data);

        await notifyParentOfChange();

        return response?.data;
      } catch (err) {
        console.error("Failed to update payment request draft:", err);

        setError(
          err?.response?.data?.detail ||
            "Unable to update payment request draft.",
        );

        throw err;
      } finally {
        setSavingRequest(false);
      }
    },
    [notifyParentOfChange],
  );

  /*
   * ==========================================================================
   * UPDATE PAYMENT REQUEST
   * ==========================================================================
   */

  const updatePaymentRequest = useCallback(
    async (id, data) => {
      if (!id) {
        throw new Error("Payment request ID is required.");
      }

      console.log(
        "[PaymentRequestManager] UPDATE START",
        {
          id,
          data,
        }
      );

      try {
        setSavingRequest(true);
        setError(null);

        console.log(
          "[PaymentRequestManager] Calling API:",
          `/payments/ca/requests/${id}/`
        );

        const response = await caApi.updatePaymentRequest(
          id,
          data
        );

        console.log(
          "[PaymentRequestManager] UPDATE SUCCESS:",
          response
        );

        console.log(
          "[PaymentRequestManager] UPDATE DATA:",
          response?.data
        );

        await notifyParentOfChange();

        console.log(
          "[PaymentRequestManager] Dashboard refresh completed"
        );

        return response?.data;
      } catch (err) {
        console.error(
          "[PaymentRequestManager] UPDATE FAILED:",
          err
        );

        console.error(
          "[PaymentRequestManager] STATUS:",
          err?.response?.status
        );

        console.error(
          "[PaymentRequestManager] SERVER RESPONSE:",
          err?.response?.data
        );

        setError(
          err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.response?.data?.non_field_errors?.[0] ||
            "Unable to update payment request."
        );

        throw err;
      } finally {
        setSavingRequest(false);
      }
    },
    [notifyParentOfChange]
  );

  /*
   * ==========================================================================
   * DELETE PAYMENT REQUEST
   * ==========================================================================
   */

  const deletePaymentRequest = useCallback(
    async (row) => {
      if (!row?.id) {
        console.error(
          "Cannot delete payment request because the request ID is missing.",
        );

        return;
      }

      const confirmed = window.confirm(
        `Are you sure you want to delete "${
          row.title || "this payment request"
        }"?`,
      );

      if (!confirmed) {
        return;
      }

      try {
        setDeletingRequest(true);
        setError(null);

        await caApi.deletePaymentRequest(row.id);

        if (selectedPaymentRequest?.id === row.id) {
          closeModal();
        }

        await notifyParentOfChange();
      } catch (err) {
        console.error("Failed to delete payment request:", err);

        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to delete payment request.";

        setError(message);

        window.alert(message);

        throw err;
      } finally {
        setDeletingRequest(false);
      }
    },
    [selectedPaymentRequest?.id, closeModal, notifyParentOfChange],
  );

  /*
   * ==========================================================================
   * MODAL CALLBACKS
   * ==========================================================================
   */

  const handleCreated = useCallback(async () => {
    closeModal();

    await notifyParentOfChange();
  }, [closeModal, notifyParentOfChange]);

  const handleUpdated = useCallback(async () => {
    closeModal();

    await notifyParentOfChange();
  }, [closeModal, notifyParentOfChange]);

  /*
   * ==========================================================================
   * MANAGER VALUE
   * ==========================================================================
   */

  const managerValue = {
    paymentOverview,
    platformOverview,
    paymentRequests,

    loadingOverview,
    loadingPlatformOverview,
    loadingPaymentRequests,
    loadingRequest,
    savingRequest,
    deletingRequest,

    loading:
      loadingOverview || loadingPlatformOverview || loadingPaymentRequests,

    error,

    selectedPaymentRequest,
    isPaymentRequestModalOpen,

    loadPaymentOverview,
    loadPlatformOverview,
    loadPaymentRequests,
    loadInitialData,

    openCreate,
    openExisting,
    closeModal,

    createPaymentRequest,
    createPaymentRequestDraft,
    retrievePaymentRequest,
    updatePaymentRequestDraft,
    updatePaymentRequest,
    deletePaymentRequest,

    handleCreated,
    handleUpdated,
  };

  /*
   * ==========================================================================
   * RENDER
   * ==========================================================================
   */

  return (
    <>
      {typeof children === "function" ? children(managerValue) : children}

        <PaymentRequestModal
            isOpen={isPaymentRequestModalOpen}
            onClose={closeModal}
            onCreated={handleCreated}
            onUpdated={handleUpdated}
            paymentRequest={selectedPaymentRequest}
            createPaymentRequest={createPaymentRequest}
            createPaymentRequestDraft={createPaymentRequestDraft}
            updatePaymentRequestDraft={updatePaymentRequestDraft}
            updatePaymentRequest={updatePaymentRequest}
        />

      {loadingRequest && (
        <div className="payment-request-loading-overlay">
          <div className="dashboard-loader" />

          <p>Loading payment request...</p>
        </div>
      )}
    </>
  );
};

export default PaymentRequestManager;
